import React, { useCallback, useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import { api } from '../../api';
import toast from 'react-hot-toast';
import { exportAttendancePdf } from '../../utils/attendancePdf';
import {
  ClipboardCheck,
  Download,
  FileText,
  Filter,
  Lock,
  MapPin,
  Plus,
  RefreshCw,
  Trash2,
  Unlock,
  Users,
  X
} from 'lucide-react';
import { formatDate, formatDateTime } from '../../utils/format';

interface Sheet {
  _id: string;
  course: { _id: string; title: string } | string | null;
  title: string;
  date: string;
  period: string;
  startTime: string;
  endTime: string;
  modality: string;
  location: string;
  trainerName: string;
  status: string;
  stats?: { expected: number; signed: number; missing: number };
  closedAt?: string | null;
}

interface Participant {
  _id: string;
  student: { _id: string; name: string; email: string } | string | null;
  status: string;
  signed: boolean;
  signedAt: string | null;
  method: string;
  comment: string;
  signature: string;
}

interface SheetDetail {
  success: boolean;
  sheet: Sheet;
  participants: Participant[];
  stats: { expected: number; signed: number; present: number; retard: number; absent: number; excuse: number; missing: number };
}

interface CourseOption {
  _id: string;
  title: string;
}

const PERIOD_LABELS: Record<string, string> = { matin: 'Matin', 'apres-midi': 'Après-midi', journee: 'Journée' };
const MODALITY_LABELS: Record<string, string> = { presentiel: 'Présentiel', distanciel: 'Distanciel', mixte: 'Mixte' };
const STATUS_LABELS: Record<string, string> = { present: 'Présent', retard: 'En retard', absent: 'Absent', excuse: 'Excusé' };

const courseLabel = (course: Sheet['course']): string => {
  if (!course) return '—';
  if (typeof course === 'string') return course;
  return course.title || '—';
};

const studentLabel = (student: Participant['student']): string => {
  if (!student) return '—';
  if (typeof student === 'string') return student;
  return student.name || student.email || '—';
};

const studentEmail = (student: Participant['student']): string => {
  if (!student || typeof student === 'string') return '';
  return student.email || '';
};

const studentId = (student: Participant['student']): string => {
  if (!student) return '';
  if (typeof student === 'string') return student;
  return student._id;
};

interface SheetFormState {
  course: string;
  title: string;
  date: string;
  period: string;
  startTime: string;
  endTime: string;
  modality: string;
  location: string;
  trainerName: string;
  notes: string;
}

const emptyForm = (): SheetFormState => ({
  course: '',
  title: '',
  date: new Date().toISOString().slice(0, 10),
  period: 'matin',
  startTime: '09:00',
  endTime: '12:30',
  modality: 'presentiel',
  location: '',
  trainerName: '',
  notes: ''
});

/**
 * « Émargements » côté administrateur — vue d'ensemble de toutes les formations,
 * création, saisie manuelle, clôture/réouverture, suppression et export PDF.
 * Route : /admin/attendance — API : /api/attendance
 */
const AdminAttendance: React.FC = () => {
  const [sheets, setSheets] = useState<Sheet[]>([]);
  const [courses, setCourses] = useState<CourseOption[]>([]);
  const [courseFilter, setCourseFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState<SheetFormState>(emptyForm());
  const [creating, setCreating] = useState(false);
  const [detail, setDetail] = useState<SheetDetail | null>(null);
  const [drafts, setDrafts] = useState<Record<string, { status: string; comment: string }>>({});
  const [rowBusy, setRowBusy] = useState('');
  const [pdfBusy, setPdfBusy] = useState('');
  const [deleting, setDeleting] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string> = {};
      if (courseFilter) params.course = courseFilter;
      const response = await api.get('/attendance', { params });
      setSheets(response.data?.sheets || []);
    } catch (error) {
      console.error('Erreur chargement émargements:', error);
    } finally {
      setLoading(false);
    }
  }, [courseFilter]);

  const loadCourses = useCallback(async () => {
    try {
      const response = await api.get('/courses/list/all');
      setCourses(response.data?.courses || []);
    } catch (error) {
      console.error('Erreur chargement des formations:', error);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadCourses();
  }, [loadCourses]);

  const openDetail = useCallback(async (sheetId: string) => {
    try {
      const response = await api.get(`/attendance/${sheetId}`);
      const data: SheetDetail = response.data;
      setDetail(data);
      const nextDrafts: Record<string, { status: string; comment: string }> = {};
      (data.participants || []).forEach((participant) => {
        nextDrafts[participant._id] = { status: participant.status, comment: participant.comment || '' };
      });
      setDrafts(nextDrafts);
    } catch (error) {
      console.error('Erreur chargement du détail:', error);
    }
  }, []);

  const handleCreate = async () => {
    if (!form.course) {
      toast.error('Choisissez une formation');
      return;
    }
    if (!form.title.trim()) {
      toast.error("Renseignez l'intitulé de la demi-journée");
      return;
    }
    try {
      setCreating(true);
      const response = await api.post('/attendance', {
        ...form,
        date: new Date(`${form.date}T12:00:00`).toISOString()
      });
      toast.success(`Demi-journée créée (${response.data?.participants || 0} participant(s) attendu(s))`);
      setShowCreate(false);
      setForm(emptyForm());
      await load();
    } catch (error) {
      console.error('Erreur création demi-journée:', error);
    } finally {
      setCreating(false);
    }
  };

  const handleClose = async (sheetId: string) => {
    try {
      await api.post(`/attendance/${sheetId}/close`);
      toast.success('Demi-journée clôturée');
      await load();
      if (detail && detail.sheet._id === sheetId) await openDetail(sheetId);
    } catch (error) {
      console.error('Erreur clôture:', error);
    }
  };

  const handleReopen = async (sheetId: string) => {
    try {
      await api.post(`/attendance/${sheetId}/reopen`);
      toast.success('Demi-journée rouverte');
      await load();
      if (detail && detail.sheet._id === sheetId) await openDetail(sheetId);
    } catch (error) {
      console.error('Erreur réouverture:', error);
    }
  };

  const handleDelete = async (sheetId: string) => {
    try {
      setDeleting(sheetId);
      await api.delete(`/attendance/${sheetId}`);
      toast.success("Feuille d'émargement supprimée");
      if (detail && detail.sheet._id === sheetId) setDetail(null);
      await load();
    } catch (error) {
      console.error('Erreur suppression:', error);
    } finally {
      setDeleting('');
    }
  };

  const handleManualEntry = async (participant: Participant) => {
    const draft = drafts[participant._id];
    if (!draft || !detail) return;
    const sid = studentId(participant.student);
    if (!sid) {
      toast.error('Apprenant inconnu');
      return;
    }
    try {
      setRowBusy(participant._id);
      await api.post(`/attendance/${detail.sheet._id}/entries`, {
        studentId: sid,
        status: draft.status,
        comment: draft.comment
      });
      toast.success('Saisie enregistrée');
      await openDetail(detail.sheet._id);
    } catch (error) {
      console.error('Erreur saisie manuelle:', error);
    } finally {
      setRowBusy('');
    }
  };

  const handleExportPdf = async (sheetId: string) => {
    try {
      setPdfBusy(sheetId);
      const response = await api.get(`/attendance/${sheetId}`);
      const data: SheetDetail = response.data;
      const filename = await exportAttendancePdf({
        courseTitle: courseLabel(data.sheet.course),
        sheetTitle: data.sheet.title,
        date: data.sheet.date,
        period: data.sheet.period,
        startTime: data.sheet.startTime,
        endTime: data.sheet.endTime,
        modality: data.sheet.modality,
        location: data.sheet.location,
        trainerName: data.sheet.trainerName,
        participants: (data.participants || []).map((participant) => ({
          name: studentLabel(participant.student),
          email: studentEmail(participant.student),
          status: participant.status,
          signature: participant.signature,
          signedAt: participant.signedAt,
          method: participant.method,
          comment: participant.comment
        }))
      });
      toast.success(`PDF généré : ${filename}`);
    } catch (error) {
      console.error('Erreur export PDF:', error);
      toast.error('Échec de la génération du PDF');
    } finally {
      setPdfBusy('');
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Émargements</h1>
            <p className="mt-1 text-sm text-gray-500">
              Toutes les feuilles d'émargement de l'organisme — {sheets.length} demi-journée(s)
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={load}
              className="flex items-center rounded-lg border border-gray-300 px-4 py-2 text-gray-700 transition-colors hover:bg-gray-50"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Rafraîchir
            </button>
            <button
              onClick={() => {
                setForm(emptyForm());
                setShowCreate(true);
              }}
              className="flex items-center rounded-lg bg-brand-600 px-4 py-2 text-white transition-colors hover:bg-brand-700"
            >
              <Plus className="mr-2 h-4 w-4" />
              Nouvelle demi-journée
            </button>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex items-center">
            <Filter className="mr-2 h-4 w-4 text-gray-500" />
            <label className="mr-3 text-sm font-medium text-gray-700">Formation</label>
            <select
              value={courseFilter}
              onChange={(event) => setCourseFilter(event.target.value)}
              className="w-full max-w-md rounded-lg border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="">Toutes les formations</option>
              {courses.map((course) => (
                <option key={course._id} value={course._id}>
                  {course.title}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-brand-600"></div>
            </div>
          ) : sheets.length === 0 ? (
            <div className="py-12 text-center">
              <ClipboardCheck className="mx-auto mb-4 h-16 w-16 text-gray-300" />
              <h3 className="mb-2 text-lg font-medium text-gray-900">Aucune feuille d'émargement</h3>
              <p className="text-gray-500">Aucune demi-journée enregistrée pour ce filtre.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Formation</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Demi-journée</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Formateur</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Signatures</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Statut</th>
                    <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {sheets.map((sheet) => (
                    <tr key={sheet._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{courseLabel(sheet.course)}</td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{sheet.title}</div>
                        <div className="mt-1 flex items-center text-xs text-gray-500">
                          {PERIOD_LABELS[sheet.period] || sheet.period}
                          {sheet.location ? (
                            <span className="ml-3 flex items-center">
                              <MapPin className="mr-1 h-3.5 w-3.5" />
                              {sheet.location}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{formatDate(sheet.date)}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{sheet.trainerName || '—'}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {sheet.stats ? `${sheet.stats.signed}/${sheet.stats.expected}` : '—'}
                      </td>
                      <td className="px-6 py-4">
                        {sheet.status === 'closed' ? (
                          <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">
                            <Lock className="mr-1 h-3 w-3" />
                            Clôturée
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                            Ouverte
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => openDetail(sheet._id)}
                            className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50"
                          >
                            Détail
                          </button>
                          <button
                            onClick={() => handleExportPdf(sheet._id)}
                            disabled={pdfBusy === sheet._id}
                            className="flex items-center rounded-lg border border-brand-200 px-3 py-1.5 text-xs font-medium text-brand-700 transition-colors hover:bg-brand-50 disabled:opacity-50"
                            title="Exporter la feuille d'émargement (PDF)"
                          >
                            <Download className="mr-1 h-3.5 w-3.5" />
                            PDF
                          </button>
                          {sheet.status === 'open' ? (
                            <button
                              onClick={() => handleClose(sheet._id)}
                              className="flex items-center rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50"
                            >
                              <Lock className="mr-1 h-3.5 w-3.5" />
                              Clôturer
                            </button>
                          ) : (
                            <button
                              onClick={() => handleReopen(sheet._id)}
                              className="flex items-center rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50"
                            >
                              <Unlock className="mr-1 h-3.5 w-3.5" />
                              Rouvrir
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(sheet._id)}
                            disabled={deleting === sheet._id}
                            className="flex items-center rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-700 transition-colors hover:bg-red-50 disabled:opacity-50"
                          >
                            <Trash2 className="mr-1 h-3.5 w-3.5" />
                            Supprimer
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modale de création */}
      {showCreate ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="max-h-full w-full max-w-2xl overflow-y-auto rounded-lg bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900">Nouvelle demi-journée</h2>
              <button onClick={() => setShowCreate(false)} className="text-gray-400 transition-colors hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="grid grid-cols-1 gap-4 px-6 py-5 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="mb-1 block text-sm font-medium text-gray-700">Formation</label>
                <select
                  value={form.course}
                  onChange={(event) => setForm({ ...form, course: event.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                >
                  <option value="">— Choisir —</option>
                  {courses.map((course) => (
                    <option key={course._id} value={course._id}>
                      {course.title}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="mb-1 block text-sm font-medium text-gray-700">Intitulé</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(event) => setForm({ ...form, title: event.target.value })}
                  placeholder="Journée 1 — matin"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Date</label>
                <input
                  type="date"
                  value={form.date}
                  onChange={(event) => setForm({ ...form, date: event.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Période</label>
                <select
                  value={form.period}
                  onChange={(event) => setForm({ ...form, period: event.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                >
                  <option value="matin">Matin</option>
                  <option value="apres-midi">Après-midi</option>
                  <option value="journee">Journée</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Heure de début</label>
                <input
                  type="time"
                  value={form.startTime}
                  onChange={(event) => setForm({ ...form, startTime: event.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Heure de fin</label>
                <input
                  type="time"
                  value={form.endTime}
                  onChange={(event) => setForm({ ...form, endTime: event.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Modalité</label>
                <select
                  value={form.modality}
                  onChange={(event) => setForm({ ...form, modality: event.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                >
                  <option value="presentiel">Présentiel</option>
                  <option value="distanciel">Distanciel</option>
                  <option value="mixte">Mixte</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Lieu</label>
                <input
                  type="text"
                  value={form.location}
                  onChange={(event) => setForm({ ...form, location: event.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                />
              </div>
              <div className="md:col-span-2">
                <label className="mb-1 block text-sm font-medium text-gray-700">Formateur (nom figé pour le PDF)</label>
                <input
                  type="text"
                  value={form.trainerName}
                  onChange={(event) => setForm({ ...form, trainerName: event.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 border-t border-gray-200 px-6 py-4">
              <button
                onClick={() => setShowCreate(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 transition-colors hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleCreate}
                disabled={creating}
                className="rounded-lg bg-brand-600 px-4 py-2 font-medium text-white transition-colors hover:bg-brand-700 disabled:opacity-50"
              >
                {creating ? 'Création...' : 'Créer la demi-journée'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Modale de détail */}
      {detail ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="max-h-full w-full max-w-5xl overflow-y-auto rounded-lg bg-white shadow-xl">
            <div className="flex items-start justify-between border-b border-gray-200 px-6 py-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{detail.sheet.title}</h2>
                <p className="mt-1 text-sm text-gray-500">
                  {courseLabel(detail.sheet.course)} — {formatDate(detail.sheet.date)} —{' '}
                  {PERIOD_LABELS[detail.sheet.period] || detail.sheet.period} — {detail.stats.signed}/{detail.stats.expected} signé(s)
                </p>
              </div>
              <div className="flex items-center space-x-3">
                {detail.sheet.status === 'open' ? (
                  <button
                    onClick={() => handleClose(detail.sheet._id)}
                    className="flex items-center rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50"
                  >
                    <Lock className="mr-1 h-3.5 w-3.5" />
                    Clôturer
                  </button>
                ) : (
                  <button
                    onClick={() => handleReopen(detail.sheet._id)}
                    className="flex items-center rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50"
                  >
                    <Unlock className="mr-1 h-3.5 w-3.5" />
                    Rouvrir
                  </button>
                )}
                <button
                  onClick={() => handleExportPdf(detail.sheet._id)}
                  disabled={pdfBusy === detail.sheet._id}
                  className="flex items-center rounded-lg border border-brand-200 px-3 py-1.5 text-xs font-medium text-brand-700 transition-colors hover:bg-brand-50 disabled:opacity-50"
                >
                  <FileText className="mr-1 h-3.5 w-3.5" />
                  PDF
                </button>
                <button onClick={() => setDetail(null)} className="text-gray-400 transition-colors hover:text-gray-600">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="px-6 py-5">
              <div className="mb-4 grid grid-cols-2 gap-4 md:grid-cols-4">
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <p className="text-xs uppercase text-gray-500">Attendus</p>
                  <p className="text-xl font-bold text-gray-900">{detail.stats.expected}</p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <p className="text-xs uppercase text-gray-500">Signés</p>
                  <p className="text-xl font-bold text-gray-900">{detail.stats.signed}</p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <p className="text-xs uppercase text-gray-500">Présents</p>
                  <p className="text-xl font-bold text-gray-900">{detail.stats.present}</p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <p className="text-xs uppercase text-gray-500">Absents</p>
                  <p className="text-xl font-bold text-gray-900">{detail.stats.absent}</p>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Apprenant</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Signature</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Horodatage</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Saisie manuelle</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {detail.participants.map((participant) => (
                      <tr key={participant._id}>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-gray-900">{studentLabel(participant.student)}</div>
                          <div className="text-xs text-gray-500">{studentEmail(participant.student)}</div>
                          <div className="mt-1 text-xs">
                            {participant.signed ? (
                              <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 font-medium text-green-800">
                                {STATUS_LABELS[participant.status] || participant.status}
                              </span>
                            ) : (
                              <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 font-medium text-gray-700">
                                Non signé
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {participant.signature && participant.signature.startsWith('data:image/') ? (
                            <img
                              src={participant.signature}
                              alt={`Signature ${studentLabel(participant.student)}`}
                              className="h-12 w-32 rounded border border-gray-200 bg-white object-contain"
                            />
                          ) : (
                            <span className="text-xs text-gray-400">
                              {participant.method === 'manual' ? 'Saisie formateur' : '—'}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {participant.signedAt ? formatDateTime(participant.signedAt) : '—'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col space-y-2 md:flex-row md:items-center md:space-x-2 md:space-y-0">
                            <select
                              value={drafts[participant._id]?.status || participant.status}
                              onChange={(event) =>
                                setDrafts({
                                  ...drafts,
                                  [participant._id]: {
                                    status: event.target.value,
                                    comment: drafts[participant._id]?.comment || ''
                                  }
                                })
                              }
                              className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
                            >
                              <option value="present">Présent</option>
                              <option value="retard">En retard</option>
                              <option value="absent">Absent</option>
                              <option value="excuse">Excusé</option>
                            </select>
                            <input
                              type="text"
                              value={drafts[participant._id]?.comment || ''}
                              onChange={(event) =>
                                setDrafts({
                                  ...drafts,
                                  [participant._id]: {
                                    status: drafts[participant._id]?.status || participant.status,
                                    comment: event.target.value
                                  }
                                })
                              }
                              placeholder="Commentaire"
                              className="w-40 rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
                            />
                            <button
                              onClick={() => handleManualEntry(participant)}
                              disabled={rowBusy === participant._id}
                              className="rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-brand-700 disabled:opacity-50"
                            >
                              {rowBusy === participant._id ? '...' : 'Enregistrer'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {detail.participants.length === 0 ? (
                <div className="py-8 text-center text-sm text-gray-500">
                  <Users className="mx-auto mb-2 h-10 w-10 text-gray-300" />
                  Aucun participant attendu pour cette demi-journée.
                </div>
              ) : null}
            </div>

            <div className="flex items-center justify-between border-t border-gray-200 px-6 py-4 text-xs text-gray-500">
              <span>{MODALITY_LABELS[detail.sheet.modality] || detail.sheet.modality}</span>
              <span>{detail.sheet.status === 'closed' ? 'Feuille clôturée' : 'Feuille ouverte'}</span>
            </div>
          </div>
        </div>
      ) : null}
    </Layout>
  );
};

export default AdminAttendance;
